import React, { useCallback } from 'react';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { toastError, toastSuccess } from '../../../utils/toast';
import i18n from '../../../i18n';
import type { BulkPlacementPreview } from './types';

interface CreateBulkMutationArgs {
  saveId: string;
  plotId: string;
  fieldId?: string;
  data: {
    cropId: string;
    cells: Array<{ row: number; col: number }>;
  };
}

interface UseBulkPlacementHandlersParams {
  plotId?: string;
  fieldId?: string;
  saveId: string;
  farmId: string;
  selectedCropForBulk: string | null;
  bulkPlacementPreview: BulkPlacementPreview | null;
  createBulkBedsWithPlantings: (args: CreateBulkMutationArgs) => { unwrap: () => Promise<any> };
  onPatch: (ops: Array<{ op: 'paintCells'; cells: Array<any> }>) => Promise<void>;
  setBulkPlacementModalVisible: (open: boolean) => void;
  setSelectedCropForBulk: (cropId: string | null) => void;
  setBulkGridDimensions: (dims: { rows: number; cols: number } | null) => void;
  setIsBulkPlacementMode: (enabled: boolean) => void;
  setBulkPlacementHoverPos: (pos: { row: number; col: number } | null) => void;
}

export const useBulkPlacementHandlers = ({
  plotId,
  fieldId,
  saveId,
  farmId,
  selectedCropForBulk,
  bulkPlacementPreview,
  createBulkBedsWithPlantings,
  onPatch,
  setBulkPlacementModalVisible,
  setSelectedCropForBulk,
  setBulkGridDimensions,
  setIsBulkPlacementMode,
  setBulkPlacementHoverPos,
}: UseBulkPlacementHandlersParams) => {
  const handleOpenBulkPlacementModal = useCallback(() => {
    setBulkPlacementModalVisible(true);
  }, [setBulkPlacementModalVisible]);

  const handleBulkPlacementConfirm = useCallback((params: { cropId: string; gridRows: number; gridCols: number }) => {
    setSelectedCropForBulk(params.cropId);
    setBulkGridDimensions({ rows: params.gridRows, cols: params.gridCols });
    setIsBulkPlacementMode(true);
    setBulkPlacementModalVisible(false);
  }, [setBulkGridDimensions, setBulkPlacementModalVisible, setIsBulkPlacementMode, setSelectedCropForBulk]);

  const handleCancelBulkPlacement = useCallback(() => {
    setIsBulkPlacementMode(false);
    setBulkGridDimensions(null);
    setSelectedCropForBulk(null);
    setBulkPlacementHoverPos(null);
  }, [setBulkGridDimensions, setBulkPlacementHoverPos, setIsBulkPlacementMode, setSelectedCropForBulk]);

  const handleConfirmBulkPlacement = useCallback(() => {
    if (!bulkPlacementPreview?.isValid || !selectedCropForBulk || !plotId) {
      if (!plotId) toastError(i18n.t('planning:bulkPlacement.noPlot'));
      return;
    }

    Modal.confirm({
      title: i18n.t('planning:bulkPlacement.confirmTitle'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>{i18n.t('planning:bulkPlacement.willCreate', { count: bulkPlacementPreview.rows * bulkPlacementPreview.cols })}</p>
          <p style={{ marginTop: 8, color: '#666' }}>
            {i18n.t('planning:bulkPlacement.position', {
              minRow: bulkPlacementPreview.minRow,
              minCol: bulkPlacementPreview.minCol,
              maxRow: bulkPlacementPreview.maxRow,
              maxCol: bulkPlacementPreview.maxCol,
            })}
          </p>
        </div>
      ),
      okText: i18n.t('common:actions.create'),
      okType: 'primary',
      cancelText: i18n.t('common:actions.cancel'),
      onOk: async () => {
        const apiCells = bulkPlacementPreview.previewCells.map((key) => {
          const [rowStr, colStr] = key.split(':');
          return { row: parseInt(rowStr, 10), col: parseInt(colStr, 10) };
        });

        try {
          const result = await createBulkBedsWithPlantings({
            saveId,
            plotId,
            fieldId: fieldId ? String(fieldId) : undefined,
            data: { cropId: selectedCropForBulk, cells: apiCells },
          }).unwrap();

          const createdBeds = Number(result?.createdBeds ?? result?.beds?.length ?? 0);
          const createdPlantings = Number(result?.createdPlantings ?? result?.plantings?.length ?? createdBeds);
          toastSuccess(i18n.t('planning:bulkPlacement.created', {
            beds: i18n.t('planning:bulkPlacement.beds', { count: createdBeds }),
            plantings: i18n.t('planning:bulkPlacement.plantings', { count: createdPlantings }),
          }));
          handleCancelBulkPlacement();
          await onPatch([{ op: 'paintCells', cells: [] }]);
        } catch (error: any) {
          toastError(error?.data?.message || i18n.t('planning:bulkPlacement.createFailed'));
        }
      },
    });
  }, [
    bulkPlacementPreview,
    createBulkBedsWithPlantings,
    fieldId,
    handleCancelBulkPlacement,
    onPatch,
    plotId,
    saveId,
    selectedCropForBulk,
  ]);

  return { handleOpenBulkPlacementModal, handleBulkPlacementConfirm, handleCancelBulkPlacement, handleConfirmBulkPlacement };
};
